/* Shopify owns discount eligibility and totals. Never infer an applied discount from display prices. */
if (!window.KaDiscountSession) {
  window.KaDiscountSession = (() => {
    let cart, reading, queue = Promise.resolve();
    const pending = new Map();
    const root = () => window.Shopify?.routes?.root || '/';
    const notify = () => document.dispatchEvent(new CustomEvent('ka:discount-state'));
    const request = async (path, options = {}) => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(root() + path, { credentials: 'same-origin', signal: controller.signal, ...options });
        if (!response.ok) throw new Error('Discount request failed');
        const result = await response.json();
        if (!Array.isArray(result.discount_codes)) throw new Error('Discount confirmation unavailable');
        return result;
      } finally { clearTimeout(timeout); }
    };
    const read = () => {
      if (!reading) reading = request('cart.js', {cache: 'no-store'})
        .then(result => { cart = result; notify(); return result; })
        .finally(() => { reading = null; });
      return reading;
    };
    const state = code => {
      const discount = cart?.discount_codes.find(d => d.code.toUpperCase() === code.toUpperCase());
      return pending.has(code.toUpperCase()) ? 'pending' : discount ? (discount.applicable ? 'applied' : 'saved') : 'idle';
    };
    const apply = code => {
      const key = code.toUpperCase();
      if (pending.has(key)) return pending.get(key);
      const operation = queue.catch(() => {}).then(async () => {
        // Read immediately before updating: retain other codes already saved to this cart.
        const current = await read();
        const codes = [...new Map([...current.discount_codes.map(d => d.code), code].map(c => [c.toUpperCase(), c])).values()];
        const result = await request('cart/update.js', {
          method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({discount: codes.join(',')})
        });
        cart = result;
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {bubbles: true}));
        if (!result.discount_codes.some(d => d.code.toUpperCase() === key)) throw new Error('Code was not saved');
        return result;
      }).finally(() => { pending.delete(key); notify(); });
      pending.set(key, operation); queue = operation; notify();
      return operation;
    };
    for (const event of ['cart:item-added', 'cart:refresh', 'cart-drawer:open', 'line-item:change:end', 'shopify:cart:lines-update']) {
      document.addEventListener(event, () => { read().catch(() => {}); });
    }
    return {read, state, apply};
  })();
}

if (!customElements.get('ka-discount-apply')) {
  class KaDiscountApply extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.label = this.querySelector('[data-discount-label]');
      this.status = this.querySelector('[data-discount-status]');
      if (!this.button || !this.label || !this.dataset.code) return;
      this.controller = new AbortController();
      const options = {signal: this.controller.signal};
      this.button.addEventListener('click', event => { event.preventDefault(); event.stopPropagation(); this.apply(); }, options);
      document.addEventListener('ka:discount-state', () => this.render(), options);
      this.render();
      window.KaDiscountSession.read().catch(() => {});
    }
    disconnectedCallback() { this.controller?.abort(); clearTimeout(this.timer); }
    render() {
      clearTimeout(this.timer);
      const remaining = Number(this.dataset.expiresAt) * 1000 - Date.now();
      const expired = !Number.isFinite(remaining) || remaining <= 0;
      const state = window.KaDiscountSession.state(this.dataset.code);
      this.button.disabled = expired || state === 'pending';
      this.button.setAttribute('aria-busy', String(state === 'pending'));
      this.label.textContent = expired ? 'Expired' : ({pending: 'Applying…', applied: 'Applied', saved: 'Saved'})[state] || 'Apply';
      this.button.setAttribute('aria-label', `${this.label.textContent} discount code ${this.dataset.code}`);
      this.status.textContent = expired ? 'This offer has ended.' : state === 'applied' ? 'Discount applied to eligible items in your cart.' : state === 'saved' ? 'Code saved for checkout. It will apply when your cart qualifies. Check your final discount at checkout.' : '';
      if (!expired) this.timer = setTimeout(() => this.render(), Math.min(60000, remaining));
    }
    async apply() {
      if (this.button.disabled) return;
      this.render();
      if (this.button.disabled) return;
      try { await window.KaDiscountSession.apply(this.dataset.code); }
      catch (_) {
        if (!this.isConnected) return;
        this.render();
        this.label.textContent = 'Retry';
        this.status.textContent = 'Could not confirm the code was saved. Try again or enter the code at checkout.';
      }
    }
  }
  customElements.define('ka-discount-apply', KaDiscountApply);
}
